import React, { useEffect } from 'react'
import { useSelector } from 'react-redux'
import { useRecoilState, useSetRecoilState } from 'recoil'
import { useHistory } from 'react-router-dom'
import filter from 'lodash/filter'
import { selectUserDiCode } from '@landscape/ui-redux'
import {
  addNotification,
  appPreferencesState,
  dateDisplayFormat,
  downloadAnyFileFromS3,
  FILE_TYPES,
  getJWTPayload,
  inProgressQCAtom,
  notificationsState,
  NotiType,
  useDashboardContext,
} from '@landscape/ui-lib'

const { VITE_REACT_APP_WS_URL } = import.meta.env

const GET_CONNECTION_ID_WS_ROUTE = 'getConnectionId'

export const WSListener: React.FC = () => {
  const client_di_code = window.encodeURIComponent(useSelector(selectUserDiCode))
  const username = window.encodeURIComponent(getJWTPayload().cb.un)
  const history = useHistory()
  const dashboardReactContext = useDashboardContext()
  const setNotis = useSetRecoilState(notificationsState)
  const [currDownloadingQCs, setCurrDownloadingQCs] = useRecoilState(inProgressQCAtom)
  const setAppPreferences = useSetRecoilState(appPreferencesState)

  let ws, reconnectTimeout, heartbeatInterval: NodeJS.Timeout
  let shouldReconnect = true

  const DOMAIN_NAME = 'tecd'
  const cb = window.cb

  useEffect(() => {
    connect()

    return () => {
      window.removeEventListener('beforeunload', () => {
        if (ws) {
          ws.close()
          console.debug('WebSocket connection closed')
        }
      })

      shouldReconnect = false

      if (ws) {
        ws.close()
        console.debug('WebSocket connection closed')
      }
    }
  }, [client_di_code])

  const connect = () => {
    heartbeatInterval && clearInterval(heartbeatInterval)

    const awsCredentials = cb.core.iam.getTempAWSCreds(DOMAIN_NAME)
    const payload = getJWTPayload()
    if (!awsCredentials || !payload) {
      // return new Promise((resolve, reject) => {
      //   reject('Unable to authenticate. Please log out, log back in, and upload the file again.')
      // })
      // NOTE: handle error case
      return
    }
  
    ws = new WebSocket(
      `${VITE_REACT_APP_WS_URL}?username=${username}&client_di_code=${client_di_code}`
    )
    console.debug(
      `WSListener: connecting to ${VITE_REACT_APP_WS_URL}?username=${username}&client_di_code=${client_di_code} ...`
    )
    ws.addEventListener('open', (e) => {
      ws.send(JSON.stringify({ action: GET_CONNECTION_ID_WS_ROUTE }))
      // Use window if it's defined (in browsers), otherwise fall back to global (Node.js)
      const globalObject = typeof window !== 'undefined' ? window : global
      heartbeatInterval = globalObject.setInterval(() => {
        pingServer()
      }, 1 * 60000)
    })
    ws.addEventListener('message', (event) => {
      const eventData = JSON.parse(event.data)
      handleWebsocketMessage(eventData)
    })
    ws.addEventListener('close', (e) => {
      console.log(
        'websocket closed at ',
        dateDisplayFormat(new Date(Date.now())),
        ' with code ',
        e.code
      )
      heartbeatInterval && clearInterval(heartbeatInterval)
      if (shouldReconnect) {
        connect()
      }
    })
    ws.addEventListener('error', (event) => {
      console.log(event)
      heartbeatInterval && clearInterval(heartbeatInterval)
      if (shouldReconnect) {
        connect()
      }
    })
    // return () => {
    //   if (heartbeatInterval) {
    //     global.clearInterval(heartbeatInterval)
    //   }
    //   ws.close(1000)
    // }
  }

  const pingServer = () => {
    try {
      ws.send(JSON.stringify({ action: 'ping' }))
    } catch (error) {
      console.error('pingServer error', error)
      connect()
    }
  }

  const updateJobsTable = (jobId) => {
    // When a QC report is finished
    // this will remove the spinner from appropriate summarize file button
    // setCurrDownloadingQCs(currDownloadingQCs.splice(index, 1))
    setCurrDownloadingQCs(filter(currDownloadingQCs, (item) => jobId !== item))
  }

  const refreshJobsTable = () => {
    if (history.location.pathname === '/jobs' && dashboardReactContext.refetchJobs) {
      dashboardReactContext.refetchJobs()
    }
  }

  const refreshQcModal = () => {
    if (dashboardReactContext.refetchQcModal) {
      dashboardReactContext.refetchQcModal()
    }
  }

  const refreshAggTable = () => {
    if (history.location.pathname === '/agg-reports' && dashboardReactContext.refetchAggs) {
      dashboardReactContext.refetchAggs()
    }
  }

  const handleWebsocketMessage = async (data: any) => {
    try {
      const { di_code, jobId, fileName, connectionId, clientType } = data

      switch (data.type) {
        case 'keep-alive':
          // keepAliveReceivedFromServer()
          break
        case 'job_finished':
          if (jobId) {
            const message = `Job ${jobId} has finished processing!`
            const notiProps = {
              type: NotiType.SUCCESS,
              message,
              uniqueId: fileName,
            }
            // pass in the recoil setter to add notification as 2nd param
            addNotification(notiProps, setNotis)
          }
          setTimeout(() => {
            refreshJobsTable()
          }, 1500)
          break
        case 'job_updated':
          refreshJobsTable()
          break
        case 'job_failed':
          if (jobId) {
            const message = `Job ${jobId} has failed processing.`
            const notiProps = {
              type: NotiType.WARNING,
              message,
              uniqueId: fileName,
            }
            // pass in the recoil setter to add notification as 2nd param
            addNotification(notiProps, setNotis)
          }
          setTimeout(() => {
            refreshJobsTable()
          }, 1500)
          break
        case 'qc_job_finished':
          refreshQcModal()
          break
        case 'qc_csv_ready_for_download':
          if (jobId) {
            const metadata = {
              type: FILE_TYPES.QC,
              jobId,
              originalFileName: fileName,
              di_code,
              clientType,
            }
            downloadAnyFileFromS3({
              metadata,
              callback: () => updateJobsTable(jobId),
            })
          }
          break
        case 'connectionId':
          if (connectionId) {
            sessionStorage.setItem('connectionId', connectionId)
          }
          break
        case 'agg_report_updated':
          let { entryTerms, recordsCount } = data
          if (entryTerms) {
            if (recordsCount <= 0) {
              addNotification(
                {
                  type: NotiType.WARNING,
                  message: `No records found for ${entryTerms.join(', ')}`,
                  uniqueId: fileName,
                },
                setNotis
              )
            } else {
              addNotification(
                {
                  type: NotiType.SUCCESS,
                  message: `Aggregate file generated for ${entryTerms.join(', ')}!`,
                  uniqueId: fileName,
                },
                setNotis
              )
              setTimeout(() => {
                refreshAggTable()
              }, 1500)
            }
          }
          break
        case 'sync_app_preferences':
          delete data.type
          setAppPreferences({ ...data })
          break
        default:
          break
      }
    } catch (err) {
      console.error('websocket error', err)
    }
  }

  return null
}
